import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'

import type { PosValidatorDetail } from '@/types/pos-staker'
import { useIwan } from '@/utils/useWebSocket'

interface StakeInfo {
  totalStake: string
  quota: string
  activity: string
  lockPeriod: string
  status: string
  maxFee: string
  delegationFee: string
  address: string
  feeRate: string
  imgData?: string
}

const INSTANCE_STAKE_INFO: StakeInfo = {
  totalStake: '0',
  quota: '0',
  activity: '0',
  lockPeriod: '0',
  status: 'Working',
  maxFee: '0',
  delegationFee: '0',
  address: '0x',
  feeRate: '0',
}

export const useCurrentStakeInfo = () => {
  const { data } = useIwan(['getCurrentStakerInfo'], { chainType: 'WAN' }, true)
  return useMemo(() => {
    const ret: StakeInfo[] = []
    if (data) {
      data.forEach((i: any) => {
        const item = Object.assign({}, INSTANCE_STAKE_INFO)
        item.totalStake = i.clients
          .reduce(
            (prev: BigNumber, curr: any) => prev.plus(curr.amount),
            new BigNumber('0'),
          )
          .plus(i.amount)
          .dividedBy(Math.pow(10, 18))
          .toString()
        item.address = i.address
        ret.push(item)
      })
      return ret
    } else {
      return []
    }
  }, [data])
}

export const useDelegatorStakeInfo = () => {
  const { address } = useAccount()
  const { data } = useIwan(
    ['getDelegatorStakeInfo'],
    { chainType: 'WAN', address },
    !!address,
  )

  return useMemo(() => {
    if (data) {
      const ret: any = []
      data.forEach((v: any) => {
        ret.push({
          ...v,
          amount: new BigNumber(v.amount)
            .dividedBy(Math.pow(10, 18))
            .toString(10),
        })
      })
      ret.done = true
      return ret
    } else {
      return []
    }
  }, [data])
}

export const useDelegatorTotalIncentive = () => {
  const { address } = useAccount()
  const { data } = useIwan(
    ['getDelegatorTotalIncentive'],
    { chainType: 'WAN', address },
    !!address,
  )
  return useMemo(() => {
    if (data) {
      const ret: any = []
      data.forEach((v: any) => {
        ret.push({
          ...v,
          amount: new BigNumber(v.amount)
            .dividedBy(Math.pow(10, 18))
            .toString(10),
        })
      })
      return ret
    } else {
      return []
    }
  }, [data])
}

export const usePosValidatorDetail = () => {
  const { address } = useAccount()
  const { data: stakeInfo } = useIwan<PosValidatorDetail[]>(
    ['getValidatorStakeInfo'],
    {
      address,
    },
    !!address,
  )
  const { data: registeredValidator } = useIwan(
    ['getRegisteredValidator'],
    {
      address: stakeInfo && stakeInfo[0] ? stakeInfo[0].address : '',
    },
    !!(stakeInfo && stakeInfo[0]),
  )

  return useMemo(() => {
    if (stakeInfo === null) return null
    if (stakeInfo && stakeInfo[0]) {
      if (registeredValidator === null) return stakeInfo[0]
      if (registeredValidator && registeredValidator[0]) {
        stakeInfo[0].validateorNameInfo = registeredValidator[0]
        return stakeInfo[0]
      } else {
        stakeInfo[0].validateorNameInfo = null
        return stakeInfo[0]
      }
    } else {
      return null
    }
  }, [stakeInfo, registeredValidator])
}
